import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Station =
  | 'all'
  | 'expo'
  | 'grill'
  | 'saute'
  | 'fry'
  | 'salad'
  | 'dessert'
  | 'bar';

export interface TicketItem {
  id: string;
  name: string;
  quantity: number;
  modifiers: string[];
  specialInstructions?: string;
  allergenAlert?: string;
  allergens?: string[];
  station: string;
  status: 'pending' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  seatNumber?: number;
  courseNumber?: number;
}

export interface Ticket {
  id: string;
  orderId: string;
  tableNumber: string;
  tableName?: string;
  serverName: string;
  partySize: number;
  items: TicketItem[];
  createdAt: string;
  sentAt: string;
  status: 'new' | 'in_progress' | 'ready' | 'completed';
  priority: 'normal' | 'rush' | 'vip';
  courseNumber?: number;
  notes?: string;
  allergenAlert?: string;
  hasAllergenAlert?: boolean;
}

export interface KDSSettings {
  station: Station;
  alertSoundEnabled: boolean;
  warningThresholdMinutes: number;
  criticalThresholdMinutes: number;
  autoScrollEnabled: boolean;
  compactMode: boolean;
  showCompletedItems: boolean;
  columnsCount: number;
  selectedCourse: number | null; // null = all courses, number = specific course
}

interface KDSState {
  tickets: Ticket[];
  selectedStation: Station;
  settings: KDSSettings;

  // Actions
  setTickets: (tickets: Ticket[]) => void;
  addTicket: (ticket: Ticket) => void;
  updateTicket: (ticketId: string, updates: Partial<Ticket>) => void;
  removeTicket: (ticketId: string) => void;

  updateItem: (ticketId: string, itemId: string, updates: Partial<TicketItem>) => void;

  setSelectedStation: (station: Station) => void;
  updateSettings: (settings: Partial<KDSSettings>) => void;

  // Computed
  getFilteredTickets: () => Ticket[];
  getTicketAgeMinutes: (ticket: Ticket) => number;
  getTicketUrgency: (ticket: Ticket) => 'normal' | 'warning' | 'critical';
  getAvailableCourses: () => number[];
}

const defaultSettings: KDSSettings = {
  station: 'all',
  alertSoundEnabled: true,
  warningThresholdMinutes: 10,
  criticalThresholdMinutes: 15,
  autoScrollEnabled: false,
  compactMode: false,
  showCompletedItems: false,
  columnsCount: 4,
  selectedCourse: null,
};

export const useKDSStore = create<KDSState>()(
  persist(
    (set, get) => ({
      tickets: [],
      selectedStation: 'all',
      settings: defaultSettings,

      setTickets: (tickets) => set({ tickets }),

      addTicket: (ticket) =>
        set((state) => ({
          tickets: [...state.tickets, ticket],
        })),

      updateTicket: (ticketId, updates) =>
        set((state) => ({
          tickets: state.tickets.map((t) =>
            t.id === ticketId ? { ...t, ...updates } : t
          ),
        })),

      removeTicket: (ticketId) =>
        set((state) => ({
          tickets: state.tickets.filter((t) => t.id !== ticketId),
        })),

      updateItem: (ticketId, itemId, updates) =>
        set((state) => ({
          tickets: state.tickets.map((ticket) => {
            if (ticket.id !== ticketId) return ticket;

            const updatedItems = ticket.items.map((item) =>
              item.id === itemId ? { ...item, ...updates } : item
            );

            // Check if all items are completed to update ticket status
            const allCompleted = updatedItems.every(
              (item) => item.status === 'completed'
            );
            const anyInProgress = updatedItems.some(
              (item) => item.status === 'in_progress'
            );

            let ticketStatus = ticket.status;
            if (allCompleted) {
              ticketStatus = 'ready';
            } else if (anyInProgress) {
              ticketStatus = 'in_progress';
            }

            return {
              ...ticket,
              items: updatedItems,
              status: ticketStatus,
            };
          }),
        })),

      setSelectedStation: (station) => set({ selectedStation: station }),

      updateSettings: (settings) =>
        set((state) => ({
          settings: { ...state.settings, ...settings },
        })),

      getFilteredTickets: () => {
        const { tickets, selectedStation, settings } = get();
        const { selectedCourse } = settings;

        let filtered = tickets.filter((t) => t.status !== 'completed');

        // Filter by course if selected
        if (selectedCourse !== null) {
          filtered = filtered.filter((ticket) => ticket.courseNumber === selectedCourse);
        }

        if (selectedStation === 'all') {
          return filtered;
        }

        // Filter tickets that have items for the selected station
        return filtered
          .filter((ticket) =>
            ticket.items.some((item) => item.station === selectedStation)
          )
          .map((ticket) => ({
            ...ticket,
            items: ticket.items.filter(
              (item) => item.station === selectedStation
            ),
          }));
      },

      getTicketAgeMinutes: (ticket) => {
        const sentAt = new Date(ticket.sentAt);
        const now = new Date();
        return Math.floor((now.getTime() - sentAt.getTime()) / 60000);
      },

      getTicketUrgency: (ticket) => {
        const { settings } = get();
        const ageMinutes = get().getTicketAgeMinutes(ticket);

        if (ageMinutes >= settings.criticalThresholdMinutes) {
          return 'critical';
        }
        if (ageMinutes >= settings.warningThresholdMinutes) {
          return 'warning';
        }
        return 'normal';
      },

      getAvailableCourses: () => {
        const { tickets } = get();
        const courses = new Set<number>();

        tickets
          .filter((t) => t.status !== 'completed')
          .forEach((ticket) => {
            if (ticket.courseNumber && ticket.courseNumber > 0) {
              courses.add(ticket.courseNumber);
            }
          });

        return Array.from(courses).sort((a, b) => a - b);
      },
    }),
    {
      name: 'kds-storage',
      partialize: (state) => ({
        settings: state.settings,
        selectedStation: state.selectedStation,
      }),
    }
  )
);
